import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ediFileIcon from "assets/img/png/ediFilePng.png";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { SmallBody, TinyBody } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { downloadEDIFile } from "store/automatedImportStore";
import type { DTOProcessedS3File, EdiInfosJson } from "store/automatedImportStore/types";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";
interface RowActionsProps {
    row: DTOProcessedS3File;
}

export const RowActions = ({ row }: RowActionsProps) => {
    const [t] = useTranslation();
    const iconSx = { height: "1rem", width: "1rem" };
    const dispatch = useAppDispatch();
    const { s3EdiKey, s3CsvOriginalKey, s3CsvToImportKey, suggestedFileName = "" } = row;

    const options: ThreeDotMenuOptions[] = [
        {
            id: "download-edi-file",
            hide: !s3EdiKey,
            icon: <FileDownloadOutlinedIcon sx={iconSx} />,
            name: t("Settings.EDIInterforum.DownloadEDIFile"),
            action: () => dispatch(downloadEDIFile({ s3Key: s3EdiKey, suggestedFileName, extension: ".edi" })),
        },
        {
            id: "download-csv-file",
            hide: !s3CsvOriginalKey,
            icon: <FileDownloadOutlinedIcon sx={iconSx} />,
            name: t("Settings.EDIInterforum.DownloadCSVFile"),
            action: () => dispatch(downloadEDIFile({ s3Key: s3CsvOriginalKey, suggestedFileName, extension: ".csv" })),
        },
        {
            id: "download-csv-import-file",
            hide: !s3CsvToImportKey,
            icon: <FileDownloadOutlinedIcon sx={iconSx} />,
            name: t("Settings.EDIInterforum.DownloadCSVImportFile"),
            action: () => dispatch(downloadEDIFile({ s3Key: s3CsvToImportKey, suggestedFileName, extension: ".csv" })),
        },
    ];

    return <ThreeDotMenu options={options} />;
};

export const EdiFileName = ({ row }: RowActionsProps) => {
    const [t] = useTranslation();
    const { suggestedFileName = "" } = row;

    return (
        <div className={styles["edi-file-label"]}>
            <img src={ediFileIcon} alt="EDI file icon" />
            <div>
                <SmallBody title={`${t("Settings.EDIInterforum.DisplayedName")} ${suggestedFileName}`}>
                    {suggestedFileName}
                </SmallBody>
                <TinyBody italic color="grey" title={`${t("Settings.EDIInterforum.OriginalName")} ${row.fileName}`}>
                    {row.fileName}
                </TinyBody>
            </div>
        </div>
    );
};

export const RowCollapseContent = ({ row }: RowActionsProps) => {
    const [t] = useTranslation();
    const hasFinalRecord = !!row.ediInfosJson.footer;

    return (
        <div className={styles["row-collapse-content"]}>
            <SmallBody color="grey">
                {t("Settings.EDIInterforum.EDIIdentifier")} : {row.ediIdentifier}
            </SmallBody>
            <SmallBody color="grey">
                {t("Settings.EDIInterforum.FinalRecord")} :{" "}
                {hasFinalRecord ? "" : t("Settings.EDIInterforum.NoDataAvailable")}
            </SmallBody>
            {hasFinalRecord && <FinalRecordTable ediInfosJson={row.ediInfosJson} />}
        </div>
    );
};

/**
 * Format final record values based on keys
 * @param key string the key of the value to format
 * @param value string the value to format
 * @returns
 */
const formatFinalRecordValue = (key: string, value: string) => {
    switch (key) {
        case "FIN-MT-NET-HT":
        case "FIN-MT-BRUT-HT":
        case "FIN-MT-NET-HT-TL":
        case "FIN-MT-BRUT-HT-TL":
            if (+value > 0) {
                return formatNumber(+value / 100, { isCurrency: true });
            }
            return "-";
        case "FIN-QTE-FACT":
        case "FIN-NB-ENREG":
            return formatNumber(+value);
        case "FIN-DATFIC":
            // Convert yyyymmdd to dd/mm/yyyy
            if (value && value.length === 8) {
                const year = value.substring(0, 4);
                const month = value.substring(4, 6);
                const day = value.substring(6, 8);
                return `${day}/${month}/${year}`;
            }
            return value;
        default:
            return value;
    }
};

const FinalRecordTable = ({ ediInfosJson }: { ediInfosJson: EdiInfosJson }) => {
    return (
        <div className={styles["edi-infos-json-table"]}>
            {Object.entries(ediInfosJson.footer).map(([key, value]) => (
                <div className={styles["row"]} key={key}>
                    <div className={styles["label"]}>
                        <SmallBody color="grey">{key}</SmallBody>
                    </div>
                    <div className={styles["value"]}>
                        <SmallBody color="grey">{formatFinalRecordValue(key, value)}</SmallBody>
                    </div>
                </div>
            ))}
        </div>
    );
};
